import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Member {
  id: number;
  email: string;
  name: string;
  phone: string | null;
  address: string | null;
  detail_address: string | null;
  postal_code: string | null;
  pccc: string | null;
}

interface AuthContextType {
  member: Member | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithToken: (token: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, name: string, phone: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateProfile: (data: Partial<Member>) => Promise<{ success: boolean; error?: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [member, setMember] = useState<Member | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('ws_token'));
  const [loading, setLoading] = useState(true);

  // 앱 시작 시 토큰으로 사용자 정보 조회
  useEffect(() => {
    if (token) {
      const ctrl = new AbortController();
      fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
        signal: ctrl.signal,
      })
        .then(res => {
          if (!res.ok) throw new Error();
          return res.json();
        })
        .then(data => { if (!ctrl.signal.aborted) setMember(data); })
        .catch((err) => {
          if (err?.name === 'AbortError') return;
          localStorage.removeItem('ws_token');
          setToken(null);
          setMember(null);
        })
        .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
      return () => ctrl.abort();
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email: string, password: string) => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.detail || '로그인 실패' };
      localStorage.setItem('ws_token', data.token);
      setToken(data.token);
      setMember(data.member);
      return { success: true };
    } catch {
      return { success: false, error: '서버 연결에 실패했습니다' };
    }
  };

  // 네이버 · 구글에서 돌아오며 받은 토큰으로 로그인 상태를 만든다
  const loginWithToken = async (newToken: string) => {
    try {
      const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${newToken}` } });
      if (!res.ok) return { success: false, error: '로그인 정보를 확인하지 못했습니다' };
      const data = await res.json();
      localStorage.setItem('ws_token', newToken);
      setToken(newToken);
      setMember(data);
      return { success: true };
    } catch {
      return { success: false, error: '서버 연결에 실패했습니다' };
    }
  };

  const register = async (email: string, password: string, name: string, phone: string) => {
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, phone: phone || null })
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.detail || '회원가입 실패' };
      localStorage.setItem('ws_token', data.token);
      setToken(data.token);
      setMember(data.member);
      return { success: true };
    } catch {
      return { success: false, error: '서버 연결에 실패했습니다' };
    }
  };

  const logout = () => {
    localStorage.removeItem('ws_token');
    setToken(null);
    setMember(null);
  };

  const updateProfile = async (data: Partial<Member>) => {
    if (!token) return { success: false, error: '로그인이 필요합니다' };
    try {
      const res = await fetch('/api/auth/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      const result = await res.json();
      if (!res.ok) return { success: false, error: result.detail || '수정 실패' };
      setMember(result.member);
      return { success: true };
    } catch {
      return { success: false, error: '서버 연결에 실패했습니다' };
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!token) return { success: false, error: '로그인이 필요합니다' };
    try {
      const res = await fetch('/api/auth/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });
      const result = await res.json();
      if (!res.ok) return { success: false, error: result.detail || '변경 실패' };
      return { success: true };
    } catch {
      return { success: false, error: '서버 연결에 실패했습니다' };
    }
  };

  return (
    <AuthContext.Provider value={{ member, token, loading, login, loginWithToken, register, logout, updateProfile, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};
